import React, {useState, useEffect} from 'react';
import ShareholderItem from './ShareholderItem';
import LinkShareholderDetail from '@components/common/Link/LinkShareholderDetails';
import ContactService from '@services/contact.service';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import ContactIntro from '@components/ContactUs/ContactIntro';

export default function ShareholderHome({categories, shareholderContact}) {
    const {locale} = useRouter();
    const [visible, setVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        email: '',
        content: '',
        title: getLocaleContent(locale, 'page.contact-form.title_1'),
        gender: 1,
    });

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
        setErrors(prev => ({...prev, [name]: ''}));
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 🧩 Submit form
    const handleSubmit = async e => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setMessage(null);

        try {
            const contactService = new ContactService();
            const res = await contactService.sendContact(formData);
            if (res) {
                setMessage('✅ ' + getLocaleContent(locale, 'page.contact-form.success'));
                setFormData({
                    full_name: '',
                    phone_number: '',
                    email: '',
                    content: '',
                    title: getLocaleContent(locale, 'page.contact-form.title_1'),
                    gender: 1,
                });
            } else {
                setMessage('❌ ' + getLocaleContent(locale, 'page.contact-form.failed'));
            }
        } catch (error) {
            setMessage('❌ ' + getLocaleContent(locale, 'page.contact-form.error'));
        } finally {
            setLoading(false);
        }
    };

    const items = React.useMemo(() => {
        if (!Array.isArray(categories)) return [];

        return categories.map(cat => ({
            id: cat.category_id,
            title: cat.category_name,
            icon: cat.image_url || '/bw_image/ic_other_new.png',
        }));
    }, [categories]);

    const mapShareholderContact = (settings = {}) => {
        const getValue = key => settings[key]?.value || '';
        const isEN = locale === 'en';

        // TITLE
        const title = isEN ? getValue('SHAREHOLDER_TITLE_EN') : getValue('SHAREHOLDER_TITLE');

        // ===== CFO =====
        const cfoRaw = isEN ? getValue('SHAREHOLDER_CFO_EN') : getValue('SHAREHOLDER_CFO');

        const [cfoName, cfoTitle] = cfoRaw.split(/[-–]/); // hỗ trợ cả - và –

        // ===== COO =====
        const cooRaw = isEN ? getValue('SHAREHOLDER_COO_EN') : getValue('SHAREHOLDER_COO');

        const [cooName, ...cooTitleArr] = cooRaw.split(/[-–]/);
        const cooTitle = cooTitleArr.join('–');

        return {
            title,
            email: getValue('SHAREHOLDER_EMAIL'),
            phone: getValue('SHAREHOLDER_PHONE'),
            people: [
                ...(cfoName
                    ? [
                          {
                              name: cfoName.trim(),
                              title: cfoTitle?.trim() || '',
                          },
                      ]
                    : []),
                ...(cooName
                    ? [
                          {
                              name: cooName.trim(),
                              title: cooTitle.trim(),
                          },
                      ]
                    : []),
            ],
        };
    };
    const contact = mapShareholderContact(shareholderContact);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.full_name.trim()) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');
        } else if (formData.full_name.length < 2) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.name-short');
        }

        if (!formData.email.trim()) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-required');
        } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-matches');
        }

        if (!formData.phone_number.trim()) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
        } else if (!/^0(?:3[2-9]|5[25689]|7[06-9]|8[1-689]|9[0-9])[0-9]{7}$/.test(formData.phone_number)) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-matches');
        }

        if (!formData.content.trim()) {
            newErrors.content = getLocaleContent(locale, 'page.booking-form.validation.message-required');
        } else if (formData.content.length < 10) {
            newErrors.content = getLocaleContent(locale, 'page.booking-form.validation.message-matches');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return (
        <>
            <div className="shareholder-container">
                <div className="company">
                    <div className="shareholder-grid">
                        {/* Thông tin liên hệ */}
                        <div className="shareholder-contact">
                            <h2 className="contact-title">{contact.title}</h2>
                            <ul className="contact-list">
                                {contact.email && (
                                    <li className="contact-item">
                                        <img className="contact-icon" src="/bw_image/ic_mail_shareholder.svg" alt="" />
                                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                    </li>
                                )}
                                {contact.people &&
                                    contact.people.map((p, idx) => (
                                        <li className="contact-item" key={idx}>
                                            <img className="contact-icon" src="/bw_image/ic_position.svg" alt="" />
                                            <div>
                                                <strong>{p.name}</strong>
                                                {p.title && <span className="person-title"> - {p.title}</span>}
                                            </div>
                                        </li>
                                    ))}
                                {contact.phone && (
                                    <li className="contact-item">
                                        <img className="contact-icon" src="/bw_image/ic_call.svg" alt="" />
                                        <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                                    </li>
                                )}
                            </ul>
                        </div>
                        {/* Tab dọc (mobile) */}
                        <button type="button" className="contact-tab" onClick={() => setVisible(true)}>
                            {getLocaleContent(locale, 'page.contact-us.title')}
                        </button>
                        {/* Popup form */}
                        <div className={`contact-us-form ${visible ? 'show' : 'hide'}`}>
                            <button
                                type="button"
                                className={`close-btn-contact-us`}
                                aria-label={locale === 'en' ? 'Close contact form' : 'Đóng biểu mẫu liên hệ'}
                                onClick={() => {
                                    setVisible(false);
                                }}>
                                <img src="/bw_image/close-popup.png" alt="close" />
                            </button>
                            <h3>{getLocaleContent(locale, 'page.contact-form.title')}</h3>
                            <span>{getLocaleContent(locale, 'page.contact-form.description')}</span>

                            <form onSubmit={handleSubmit}>
                                <div className="info">
                                    <input
                                        name="full_name"
                                        type="text"
                                        placeholder={getLocaleContent(locale, 'page.booking-form.full-name') + ' (*)'}
                                        value={formData.full_name}
                                        onChange={handleChange}
                                        className={errors.full_name ? 'error' : ''}
                                    />
                                    {errors.full_name && <small className="error-text">{errors.full_name}</small>}
                                </div>
                                <div className="info">
                                    <input
                                        name="phone_number"
                                        type="text"
                                        maxLength={10}
                                        pattern="[0-9]*"
                                        placeholder={getLocaleContent(locale, 'page.booking-form.phone') + ' (*)'}
                                        value={formData.phone_number}
                                        onChange={handleChange}
                                        className={errors.phone_number ? 'error' : ''}
                                    />
                                    {errors.phone_number && <small className="error-text">{errors.phone_number}</small>}
                                </div>
                                <div className="info">
                                    <input
                                        name="email"
                                        type="email"
                                        placeholder={getLocaleContent(locale, 'common.email') + ' (*)'}
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'error' : ''}
                                    />
                                    {errors.email && <small className="error-text">{errors.email}</small>}
                                </div>
                                <div className="info">
                                    <textarea
                                        name="content"
                                        className={`info-contact-us-textarea ${errors.content ? 'error' : ''}`}
                                        placeholder={getLocaleContent(locale, 'page.booking-form.message') + ' (*)'}
                                        value={formData.content}
                                        onChange={handleChange}></textarea>
                                    {errors.content && <small className="error-text">{errors.content}</small>}
                                </div>

                                {message && <p className="contact-message">{message}</p>}

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading
                                        ? getLocaleContent(locale, 'page.booking-form.sending')
                                        : getLocaleContent(locale, 'page.booking-form.send')}
                                    {!loading && <img src="/bw_image/arrow-long-right-whilte.png" alt="arrow" />}
                                </button>
                            </form>
                        </div>
                        {/* Danh sách danh mục */}
                        {items.map(item => (
                            <LinkShareholderDetail key={item.id} category={item}>
                                <ShareholderItem
                                    key={item.id}
                                    icon={item.icon}
                                    title={(item.title || '').toUpperCase()}
                                />
                            </LinkShareholderDetail>
                        ))}
                    </div>
                </div>
                <div className="space"></div>
                {!visible && !isMobile && (
                    <div className="contact-intro-wrapper">
                        <ContactIntro onClick={() => setVisible(true)} />
                    </div>
                )}
            </div>
        </>
    );
}
